import React, { useContext, useEffect, useRef, useState } from "react";
import styled from "styled-components";
import { CartContext } from "../CartContext";
import emailjs from "@emailjs/browser";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { useNavigate } from "react-router-dom";
import { motion, useAnimation, useInView } from "framer-motion";
import { db } from "../firebase";
import { collection, addDoc, doc, getDoc, setDoc } from "firebase/firestore";
import { useTranslation } from "react-i18next";
import { useAuth } from "../context/AuthContext";

const CheckoutPage = ({ totalPrice }) => {
  const cart = useContext(CartContext);
  const navigate = useNavigate();
  const [shippingPrice, setShippingPrice] = useState(2);
  const [selectedCountry, setSelectedCountry] = useState("Kosove");
  const { t } = useTranslation();
  const { currentUser } = useAuth();
  const [savedAddress, setSavedAddress] = useState(null);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  useEffect(() => {
    document.title = "Checkout | Premium Perfume KS";
  }, []);

  // Load saved address for logged-in user
  useEffect(() => {
    if (currentUser) {
      const fetchAddress = async () => {
        try {
          const docRef = doc(db, "addresses", currentUser.uid);
          const docSnap = await getDoc(docRef);
          if (docSnap.exists()) {
            const data = docSnap.data();
            setSavedAddress(data);
            if (data.country) setSelectedCountry(data.country);
          }
        } catch (error) {
          console.error("Error fetching saved address:", error);
        }
      };
      fetchAddress();
    }
  }, [currentUser]);

  const productsCount = cart.items.reduce(
    (sum, product) => sum + product.quantity,
    0
  );
  const subtotal = cart.items.reduce(
    (sum, product) => sum + product.quantity * product.price,
    0
  );

  useEffect(() => {
    if (selectedCountry === "Kosove") {
      setShippingPrice(subtotal > 35 ? 0 : 2);
    } else if (selectedCountry === "Serbi") {
      setShippingPrice(8);
    } else {
      setShippingPrice(5);
    }
  }, [subtotal, selectedCountry]);

  const handlePlaceOrder = async (e) => {
    e.preventDefault();
    const form = e.target;
    if (!form.checkValidity()) {
      form.reportValidity();
      return;
    }

    const formData = new FormData(form);
    const details = {
      name: formData.get("name"),
      phoneNumber: formData.get("phoneNumber"),
      country: formData.get("country"),
      city: formData.get("city"),
      address: formData.get("address"),
      message: formData.get("message") || "",
    };

    const productDetails = cart.items
      .map(
        (product) =>
          `Parfumi: ${product.name}, Sasia: ${product.quantity}, Cmimi: ${product.price}€`
      )
      .join("\n");

    const orderDetails = {
      ...details,
      productDetails: productDetails,
      items: cart.items.map((item) => ({
        id: item.id,
        name: item.name,
        image: item.image || "",
        size: item.size,
        quantity: item.quantity,
        price: item.price,
      })),
      totalPrice: (subtotal + shippingPrice).toFixed(2),
      shippingPrice,
      userId: currentUser ? currentUser.uid : null,
      timestamp: new Date(),
    };

    try {
      // Save order to Firestore first (most important)
      const docRef = await addDoc(collection(db, "orders"), orderDetails);
      console.log("Order registered with ID: ", docRef.id);

      // Save address for logged-in user
      if (currentUser) {
        try {
          await setDoc(doc(db, "addresses", currentUser.uid), {
            name: details.name,
            phoneNumber: details.phoneNumber,
            country: details.country,
            city: details.city,
            address: details.address,
          });
        } catch (addrErr) {
          console.error("Error saving address:", addrErr);
        }
      }

      // Try to send email notification (non-blocking)
      try {
        await emailjs.send(
          "service_ayc30el",
          "template_gub0pjo",
          orderDetails,
          "jPJBym5mbDJGsiuKc"
        );
      } catch (emailErr) {
        console.error("Email notification failed:", emailErr);
      }

      navigate("/success-page");
      toast.success("Porosia u bë me sukses!");
    } catch (err) {
      console.error("Order failed:", err);
      toast.error("Order failed. Please try again.");
    }
  };

  const handleShippingChange = (e) => {
    const selectedOption = e.target.value;
    setSelectedCountry(selectedOption);
  };

  const ref = useRef(null);
  const isInView = useInView(ref, { once: true });

  const animation = useAnimation();

  useEffect(() => {
    if (isInView) {
      animation.start("visible");
    }
  }, [isInView]);

  return (
    <Container>
      <ToastContainer />
      <motion.div
        ref={ref}
        className="left"
        variants={{
          hidden: { opacity: 0, x: -300 },
          visible: { opacity: 1, x: 0 },
        }}
        initial="hidden"
        animate={animation}
        transition={{ duration: 0.4, delay: 0.3, type: "spring" }}
      >
        {cart.items.map((product) => (
          <div className="item" key={product.id + product.size}>
            <img src={product.image} alt="" />
            <span>
              <h3>{product.name}</h3>
              <h4>
                {t("checkoutSize")}: <span>{product.size}</span>
              </h4>
              <div className="quantity-controls">
                <button type="button" onClick={() => cart.removeOneFromCart(product.id)}>−</button>
                <h4>{product.quantity}</h4>
                <button type="button" onClick={() => cart.addOneToCart(product.id, product.size)}>+</button>
              </div>
              <h2>{(product.quantity * product.price).toFixed(2)}€</h2>
            </span>
          </div>
        ))}
        <div className="total">
          <span>
            <h3>{t("checkoutSubtotal")}</h3>
            <h2>{subtotal.toFixed(2)}€</h2>
          </span>
          <span>
            <h3>{t("checkoutShipping")}</h3>
            {shippingPrice === 0 ? (
              <h2>{t("checkoutFree")}</h2>
            ) : (
              <h2>{shippingPrice.toFixed(2)}€</h2>
            )}
          </span>
          <hr />
          <span>
            <h2>{t("checkoutTotal")}</h2>
            <h1>{(subtotal + shippingPrice).toFixed(2)}€</h1>
          </span>
        </div>
      </motion.div>
      <hr />
      <motion.div
        className="right"
        variants={{
          hidden: { opacity: 0, x: 300 },
          visible: { opacity: 1, x: 0 },
        }}
        initial="hidden"
        animate={animation}
        transition={{ duration: 0.4, delay: 0.3, type: "spring" }}
      >
          <form onSubmit={handlePlaceOrder}>
            <div className="contact">
              <label>{t("checkoutOrderDetails")}</label>
              <input
                name="name"
                type="text"
                placeholder={t("checkoutFullName")}
                defaultValue={savedAddress?.name || ""}
                required
              />
              <span>
                <input
                  type="phone"
                  name="phoneNumber"
                  placeholder={t("checkoutPhone")}
                  defaultValue={savedAddress?.phoneNumber || ""}
                  required
                />
                <select
                  onChange={(e) => {
                    handleShippingChange(e);
                    setSelectedCountry(e.target.value);
                  }}
                  name="country"
                  value={selectedCountry}
                  required
                >
                  <option value="Kosove">{t("countryKosovo")}</option>
                  <option value="Shqiperi">{t("countryAlbania")}</option>
                  <option value="Maqedoni">{t("countryMacedonia")}</option>
                  <option value="Serbi">{t("countrySerbia")}</option>
                </select>
              </span>
              <span>
                <input name="city" type="text" placeholder={t("checkoutCity")} defaultValue={savedAddress?.city || ""} required />
                <input
                  name="address"
                  type="text"
                  placeholder={t("checkoutAddress")}
                  defaultValue={savedAddress?.address || ""}
                  required
                />
              </span>
              <textarea
                name="message"
                cols="10"
                rows="4"
                placeholder={t("checkoutMessage")}
              ></textarea>
              <button type="submit">{t("checkoutPlaceOrder")}</button>
            </div>
          </form>
      </motion.div>
    </Container>
  );
};

const Container = styled.div`
  padding: 7em 7%;
  width: 100%;
  display: flex;
  justify-content: center;
  gap: 7%;
  margin: auto;
  max-width: 1500px;
  hr {
    height: 70vh;
    border: 1px solid #3f3f3f;
  }
  .left {
    display: flex;
    flex-direction: column;
    align-items: end;
    flex: 3;
    .item {
      display: flex;
      align-items: center;
      gap: 10px;
      padding: 10px;
      align-self: flex-start;
      border-bottom: 1px solid #575757;
      width: 100%;
      img {
        height: 80px;
        width: 100px;
        object-fit: contain;
      }
      span {
        display: flex;
        flex-direction: column;
        justify-content: space-between;
        align-items: flex-start;
        width: 100%;
        .quantity-controls {
          display: flex;
          flex-direction: row;
          align-items: center;
          gap: 10px;
          margin: 5px 0;
          button {
            width: 28px;
            height: 28px;
            border-radius: 6px;
            border: none;
            background: #4a4a4a;
            color: white;
            font-size: 18px;
            cursor: pointer;
            display: flex;
            align-items: center;
            justify-content: center;
            transition: 0.3s;
            &:hover {
              background: #666;
            }
          }
          h4 {
            font-size: 16px;
            font-weight: 400;
            color: white;
          }
        }
        h4 {
          text-transform: capitalize;
          font-size: 15px;
          font-weight: 300;
          color: gray;
          display: flex;
          justify-content: center;
          align-items: center;
          gap: 7px;
          span {
            color: #cacaca;
            font-weight: 400;
            font-size: 15px;
          }
        }
        h3 {
          font-weight: 500;
          color: #b1b1b1;
          font-size: 18px;
        }
        h2 {
          margin-top: 4px;
          font-weight: 500;
          font-size: 20px;
          color: #cecece;
        }
      }
    }
    .total {
      display: flex;
      flex-direction: column;
      gap: 10px;
      width: 100%;
      margin-top: 3em;
      hr {
        border: none;
        height: 1px;
        border-bottom: 1px solid #d8d8d8;
      }
      span {
        display: flex;
        justify-content: space-between;
        h3 {
          font-weight: 400;
          color: #c5c5c5;
        }
        h2 {
          font-weight: 500;
          font-size: 19px;
          color: #b9b9b9;
        }
        h1 {
          font-size: 20px;
          font-weight: 600;
          color: #ffffff;
        }
      }
    }
  }
  .right {
    flex: 4;
    display: flex;
    flex-direction: column;
    gap: 2em;
    .paymentMethods {
      margin-top: 1em;
      align-self: start;
      display: flex;
      flex-direction: row;
      justify-content: center;
      justify-self: start;
      flex-wrap: wrap;
      gap: 1em;
      p {
        color: gray;
      }
      span {
        display: flex;
        align-items: center;
        gap: 7px;
        p {
          font-size: 15px;
          color: #d8d8d8;
        }
      }
    }
    .cash {
      margin: 2em 0;
    }
    p {
      text-align: center;
    }
    .card {
      margin-top: 2em;
    }
    div {
      display: flex;
      flex-direction: column;
      gap: 7px;
    }
    span {
      font-weight: 400;
      display: flex;
      align-items: center;
      gap: 7px;
    }
    input,
    textarea,
    select {
      font-family: "Montserrat", sans-serif;
      outline: none;
      border: 1px solid #494949;
      font-size: 17px;
      border-radius: 7px;
      padding: 14px;
      background: none;
      color: gray;
      width: 100%;
    }
    label {
      font-weight: 500;
      font-size: 20px;
    }
    .shipping {
      background-color: var(--secondaryBackgroundColor);
      padding: 20px 30px;
      border-radius: 30px;
      display: flex;
      flex-direction: row;
      justify-content: space-between;
      align-items: center;

      span {
        display: flex;
        flex-direction: column;
        align-items: flex-start;
        gap: 10px;
        h3 {
          font-size: 20px;
          font-weight: 500;
        }
        h4 {
          font-weight: 300;
        }
      }
    }
    .payBtn {
      margin: 1em 0;
      font-size: 20px;
      padding: 13px 0;
    }
    .paypalBtns {
      display: flex;
    }
    button {
      margin: auto;
      margin-top: 10px;
      font-family: "Montserrat", sans-serif;
      font-size: 18px;
      background: #ffffff;
      color: black;
      border: none;
      border-radius: 15px;
      padding: 15px 14px;
      cursor: pointer;
      transition: 0.4s;
      &:hover {
        background: #c7c7c7;
      }
    }
  }
  @media (max-width: 700px) {
    flex-direction: column;
    gap: 2em;
    hr {
      height: 1px;
    }
  }
  @media (max-width: 450px) {
    padding: 7em 4%;
    .right {
      .paymentMethods {
        align-items: center;
        width: 100%;
        flex-direction: column;
      }
    }
  }
`;

export default CheckoutPage;
